<template>
    <div class="flex flex-col items-center gap-0.5">
        <!-- Short replies collapse the three icons into one popup menu. -->
        <button
            v-if="compact"
            ref="moreButtonRef"
            type="button"
            class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-[#ff421e] transition hover:bg-[#ff421e]/10"
            :title="t('vue.ai_chat_message_actions')"
            @click="toggleMoreMenu"
        >
            <EllipsisHorizontalIcon class="h-4 w-4" />
        </button>

        <template v-else>
            <button
                type="button"
                class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-[#ff421e] transition hover:bg-[#ff421e]/10 disabled:opacity-40"
                :title="t('vue.ai_chat_email')"
                :disabled="emailing"
                @click="openEmailDialog"
            >
                <EnvelopeIcon class="h-4 w-4" />
            </button>
            <button
                type="button"
                class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-[#ff421e] transition hover:bg-[#ff421e]/10"
                :title="t('vue.ai_chat_save')"
                @click="openDialog"
            >
                <BookmarkIcon class="h-4 w-4" />
            </button>
            <button
                ref="exportButtonRef"
                type="button"
                class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-[#ff421e] transition hover:bg-[#ff421e]/10"
                :title="t('vue.ai_chat_export')"
                @click="toggleExportMenu"
            >
                <ArrowDownTrayIcon class="h-4 w-4" />
            </button>
        </template>
    </div>

    <teleport to="body">
        <template v-if="moreMenuOpen">
            <div class="fixed inset-0 z-[590]" @click="moreMenuOpen = false" />
            <div
                class="fixed z-[600] w-48 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                :style="moreMenuStyle"
            >
                <button
                    type="button"
                    class="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50 disabled:opacity-40"
                    :disabled="emailing"
                    @click="pickFromMenu(openEmailDialog)"
                >
                    <EnvelopeIcon class="h-4 w-4 text-[#ff421e]" />
                    {{ t("vue.ai_chat_email") }}
                </button>
                <button
                    type="button"
                    class="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50"
                    @click="pickFromMenu(openDialog)"
                >
                    <BookmarkIcon class="h-4 w-4 text-[#ff421e]" />
                    {{ t("vue.ai_chat_save") }}
                </button>
                <div class="my-1 border-t border-gray-100" />
                <a
                    v-for="option in exportOptions"
                    :key="option.format"
                    :href="exportHref(option.format)"
                    :target="option.newTab ? '_blank' : undefined"
                    rel="noopener"
                    class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
                    @click="moreMenuOpen = false"
                >
                    <ArrowDownTrayIcon class="h-4 w-4 text-[#ff421e]" />
                    {{ option.label }}
                </a>
            </div>
        </template>
    </teleport>

    <teleport to="body">
        <template v-if="exportMenuOpen">
            <div
                class="fixed inset-0 z-[590]"
                @click="exportMenuOpen = false"
            />
            <div
                class="fixed z-[600] w-44 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                :style="exportMenuStyle"
            >
                <a
                    v-for="option in exportOptions"
                    :key="option.format"
                    :href="exportHref(option.format)"
                    :target="option.newTab ? '_blank' : undefined"
                    rel="noopener"
                    class="block px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
                    @click="exportMenuOpen = false"
                >
                    {{ option.label }}
                </a>
            </div>
        </template>
    </teleport>

    <AiEmailRecipientsDialog
        :open="emailDialogOpen"
        :sending="emailing"
        @close="emailDialogOpen = false"
        @send="sendEmail"
    />

    <teleport to="body">
        <div
            v-if="dialogOpen"
            class="fixed inset-0 z-[600] flex items-center justify-center bg-black/30 p-4"
            @click.self="dialogOpen = false"
        >
            <div class="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                <h3 class="mb-4 text-lg font-medium text-gray-800">
                    {{ t("vue.ai_chat_save_dialog_title") }}
                </h3>
                <TextInput
                    v-model="title"
                    :label="t('vue.ai_chat_save_title_label')"
                    :placeholder="t('vue.ai_chat_save_title_label')"
                />
                <div class="mt-6 flex justify-end gap-3">
                    <AtomicButton
                        color="colorless"
                        size="sm"
                        @click="dialogOpen = false"
                    >
                        {{ t("vue.cancel") }}
                    </AtomicButton>
                    <AtomicButton
                        color="yellow"
                        size="sm"
                        :disabled="saving || title.trim() === ''"
                        @click="save"
                    >
                        {{ t("vue.save") }}
                    </AtomicButton>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import route from "ziggy-js";
import {
    EnvelopeIcon,
    BookmarkIcon,
    ArrowDownTrayIcon,
    EllipsisHorizontalIcon,
} from "@heroicons/vue/24/outline";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";

const props = withDefaults(
    defineProps<{
        chatId: number;
        messageId: number;
        defaultTitle: string;
        compact?: boolean;
    }>(),
    { compact: false },
);

const { t } = useTranslations();

const moreMenuOpen = ref(false);
const moreButtonRef = ref<HTMLElement | null>(null);
const moreMenuStyle = ref<Record<string, string>>({});

const toggleMoreMenu = (): void => {
    if (moreMenuOpen.value) {
        moreMenuOpen.value = false;
        return;
    }
    const rect = moreButtonRef.value?.getBoundingClientRect();
    if (rect) {
        moreMenuStyle.value = {
            top: `${rect.bottom + 4}px`,
            right: `${window.innerWidth - rect.right}px`,
        };
    }
    moreMenuOpen.value = true;
};

const pickFromMenu = (action: () => void | Promise<void>): void => {
    moreMenuOpen.value = false;
    void action();
};

const emailing = ref(false);
const saving = ref(false);
const dialogOpen = ref(false);
const title = ref("");

const emailDialogOpen = ref(false);

const exportMenuOpen = ref(false);
const exportButtonRef = ref<HTMLElement | null>(null);
const exportMenuStyle = ref<Record<string, string>>({});

const exportOptions = computed(() => [
    { format: "pdf", label: t("vue.ai_chat_export_pdf"), newTab: true },
    { format: "html", label: t("vue.ai_chat_export_html"), newTab: true },
    { format: "json", label: t("vue.ai_chat_export_json"), newTab: false },
]);

const exportHref = (format: string): string =>
    route("ai-chat.messages.export", {
        chat: props.chatId,
        message: props.messageId,
        format,
    });

const toggleExportMenu = (): void => {
    if (exportMenuOpen.value) {
        exportMenuOpen.value = false;
        return;
    }
    const rect = exportButtonRef.value?.getBoundingClientRect();
    if (rect) {
        exportMenuStyle.value = {
            top: `${rect.bottom + 4}px`,
            right: `${window.innerWidth - rect.right}px`,
        };
    }
    exportMenuOpen.value = true;
};

// Recipient picking lives in the shared dialog; the subject is built server
// side so every shared answer carries the same generic header. Both actions use
// JSON endpoints (axios) so they behave identically in the full-page chat and
// the floating widget — no Inertia navigation away.
const openEmailDialog = (): void => {
    emailDialogOpen.value = true;
};

const sendEmail = async (payload: {
    recipientIds: number[];
}): Promise<void> => {
    if (emailing.value || payload.recipientIds.length === 0) {
        return;
    }
    emailing.value = true;
    try {
        await axios.post(
            route("ai-chat.api.messages.email", {
                chat: props.chatId,
                message: props.messageId,
            }),
            { recipient_ids: payload.recipientIds },
        );
        emailDialogOpen.value = false;
        create({ title: t("vue.ai_chat_email_sent"), type: "success" });
    } catch {
        create({ title: t("vue.ai_chat_email_failed"), type: "error" });
    } finally {
        emailing.value = false;
    }
};

const openDialog = (): void => {
    title.value = props.defaultTitle;
    dialogOpen.value = true;
};

const save = async (): Promise<void> => {
    if (saving.value || title.value.trim() === "") {
        return;
    }
    saving.value = true;
    try {
        await axios.post(route("ai-chat.api.content.store"), {
            chat_id: props.chatId,
            message_id: props.messageId,
            title: title.value.trim(),
        });
        dialogOpen.value = false;
        create({ title: t("vue.ai_chat_saved"), type: "success" });
    } catch {
        create({ title: t("vue.ai_chat_save_failed"), type: "error" });
    } finally {
        saving.value = false;
    }
};
</script>
